import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserOrg } from "@/hooks/useUserOrg";

/** Query key for the active org's current-member count. */
export const orgMemberCountQueryKey = (orgId: string | null) =>
  ["org-member-count", orgId] as const;

export interface UseOrgMemberCount {
  /** Current members of the active org; 0 until the count resolves. */
  memberCount: number;
  isLoading: boolean;
  orgId: string | null;
  /** Refetch and resolve the fresh count, or null when it could not be read. */
  refetch: () => Promise<number | null>;
}

/**
 * How many people are currently in the org the admin is administering.
 *
 * One keyed query rather than a private fetch per surface, because the count is
 * an input to `computeOrgProfileStrength` — the Team section is scored from it —
 * and a consumer that supplied its own number (or hardcoded 0, as the profile
 * editor's completeness widget did) reports a different percentage than the
 * admin feed card and the onboarding handoff for the same organization.
 */
export const useOrgMemberCount = (): UseOrgMemberCount => {
  const { org, isLoading: orgLoading } = useUserOrg();
  const orgId = org?.id ?? null;

  const query = useQuery({
    queryKey: orgMemberCountQueryKey(orgId),
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      // Current members only. organization_members retains rows for departed
      // members (is_current / end_date), and a brokerage whose team has all left
      // must not still score a filled Team section. Verified 2026-08-12: every
      // production row is is_current = true with a null end_date, so this
      // filter excludes nobody today and stays correct as alumni accumulate.
      const { count, error } = await supabase
        .from("organization_members")
        .select("id", { count: "exact", head: true })
        .eq("org_id", orgId!)
        .eq("is_current", true);
      if (error) throw error;
      return count ?? 0;
    },
  });

  // Destructured so the callback depends on a stable identifier rather than on
  // the whole query result object, which changes every render.
  const { refetch: refetchQuery } = query;

  const refetch = useCallback(async (): Promise<number | null> => {
    if (!orgId) return null;
    const result = await refetchQuery();
    if (result.isError) return null;
    return result.data ?? null;
  }, [orgId, refetchQuery]);

  return {
    memberCount: query.data ?? 0,
    isLoading: orgLoading || (!!orgId && query.isPending),
    orgId,
    refetch,
  };
};
